import React, { useMemo } from "react";
import HelpLink from "../help/HelpLink";
import Sparkline from "./Sparkline";
import { trendChange, trends } from "../../utils/progress";

const changeLine = (points, { lowerIsBetter = false } = {}) => {
  const change = trendChange(points);
  if (change == null) return `${points.length} ${points.length === 1 ? "session" : "sessions"} so far`;
  if (Math.abs(change) < 3) return "About steady";
  const better = lowerIsBetter ? change < 0 : change > 0;
  return `${change > 0 ? "Up" : "Down"} ${Math.abs(change)}%${better ? ", improving" : ""}`;
};

const Tile = ({ label, value, points, note, className, wide }) => (
  <div className={`grid gap-1 rounded-xl border border-border p-2.5 ${wide ? "col-span-2" : ""}`}>
    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
    <span className="text-lg font-semibold tabular-nums">{value}</span>
    <Sparkline points={points} className={className} />
    <span className="text-[11px] text-muted-foreground">{note}</span>
  </div>
);

/**
 * Efficiency and recovery over time (#203). Watts per beat and aerobic drift
 * come from steady bike efforts of 20+ minutes only, so they compare like
 * with like; recovery is the heart-rate drop in the minute after hard
 * intervals. Each tile needs two sessions; the card hides with none.
 */
const TrendsCard = ({ rows }) => {
  const t = useMemo(() => trends(rows), [rows]);
  const last = (points) => points[points.length - 1]?.value;
  const tiles = [
    t.wPerBeat.length >= 2 && (
      <Tile
        key="wpb"
        label="Watts per beat"
        value={last(t.wPerBeat).toFixed(2)}
        points={t.wPerBeat}
        note={changeLine(t.wPerBeat)}
      />
    ),
    t.drift.length >= 2 && (
      <Tile
        key="drift"
        label="Aerobic drift"
        value={`${last(t.drift)}%`}
        points={t.drift}
        className="text-blue-500"
        note={last(t.drift) < 5 ? "Under 5%: a solid base" : changeLine(t.drift, { lowerIsBetter: true })}
      />
    ),
    t.recovery.length >= 2 && (
      <Tile
        key="hrr"
        label="Heart-rate recovery"
        value={`${last(t.recovery)} bpm`}
        points={t.recovery}
        note={`Drop a minute after hard intervals · ${changeLine(t.recovery).toLowerCase()}`}
        wide
      />
    ),
  ].filter(Boolean);
  if (!tiles.length) return null;
  return (
    <section aria-label="Efficiency and recovery" className="grid gap-3 rounded-2xl border border-border bg-card p-3.5">
      <h3 className="flex items-center gap-1 text-[15px] font-bold text-white">
        Efficiency and recovery
        <HelpLink topic="progress#efficiency-and-recovery" label="About efficiency and recovery" />
      </h3>
      <div className="grid grid-cols-2 gap-2">{tiles}</div>
    </section>
  );
};

export default TrendsCard;
